import React, { useEffect, useRef, useState } from 'react';
import '../index.scss';
import { Button, notification, Form, Typography, Input, Row, Col } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import API_SERVICE from '../../../services/api-service';
import AccessControl from "../../../services/AccessControl";

const { Title } = Typography;

export default function ClaimStages(props: any) {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [count, setCount] = useState(0);
    const [stages, setStages] = useState([] as any);
    const [fetching, setFetching] = useState(true);
    const pendingSubmit = useRef(false);

    const getClaimStages = async () => {
        setFetching(true);
        try {
            const { data: { data } } = await API_SERVICE.getClaimStages();
            const mData = data.items.map((item: any, i: number) => ({ ...item, key: i }));
            setStages(mData);
            setCount(data.items.length);
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => { getClaimStages(); }, []);

    useEffect(() => {
        if (pendingSubmit.current) {
            pendingSubmit.current = false;
            form.submit();
        }
    }, [stages]);

    const addNewRow = () => {
        setStages((prev: any) => prev.concat([{ id: null, name: '', displayName: '', key: count }]));
        setCount(c => c + 1);
    };

    const handleDelete = (key: number) => {
        setStages((prev: any) => prev.filter((item: any) => item.key !== key));
    };

    const handleSave = () => {
        if (stages.length === 0) {
            pendingSubmit.current = true;
            addNewRow();
        } else {
            form.submit();
        }
    };

    if (fetching) return <div>Getting claim stages...</div>

    return (
        <div>
            <Form
                size='large'
                form={form}
                onFinish={(formValues) => {
                    const reqObj: any = {
                        claimStages: stages.map((item: any, i: number) => ({
                            id: item.id,
                            name: item.id ? item.name : (formValues['' + i]?.name ?? ''),
                            displayName: formValues['' + i]?.displayName ?? ''
                        }))
                    };
                    setSaving(true);
                    API_SERVICE.claimStagesUpdate(reqObj).then(() => {
                        notification.success({ message: 'Success' });
                        getClaimStages();
                    }).catch((e: any) => {
                        notification.error({ message: API_SERVICE.handleErrors(e) });
                    }).finally(() => {
                        setSaving(false);
                    });
                }}
                layout='vertical'
            >
                <Row gutter={20} style={{ marginBottom: 8 }}>
                    <Col md={1} />
                    <Col md={11}><Title level={5}>Actual Stage</Title></Col>
                    <Col md={12}><Title level={5}>Display Stage</Title></Col>
                </Row>
                {stages.map((item: any, index: number) => {
                    const isNew = item.id === null;
                    return (
                        <Row key={`stage-${item.key}`} gutter={20} align='top'>
                            <Col md={1} style={{ paddingTop: 8 }}>
                                {isNew && (
                                    <MinusCircleOutlined
                                        className='text-primary cursor-pointer'
                                        onClick={() => handleDelete(item.key)}
                                    />
                                )}
                            </Col>
                            <Col md={11}>
                                {isNew ? (
                                    <Form.Item
                                        name={['' + index, 'name']}
                                        rules={[{ required: true, message: 'Name is required' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder='Enter stage name' />
                                    </Form.Item>
                                ) : (
                                    <Input value={item.name} disabled style={{ marginBottom: 8 }} />
                                )}
                            </Col>
                            <Col md={12}>
                                <Form.Item
                                    name={['' + index, 'displayName']}
                                    initialValue={item.displayName}
                                    rules={[{ required: true, message: 'Display name is required' }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input style={{ width: 200 }} placeholder='Enter display name' />
                                </Form.Item>
                            </Col>
                        </Row>
                    );
                })}
            </Form>
            <br />
            <AccessControl id={236}>
                <Row>
                    <Col md={12}>
                        <Button onClick={addNewRow} shape='round' icon={<PlusOutlined />} className="theme-button primary" style={{ marginRight: 8 }}>
                            Add More
                        </Button>
                        <Button onClick={handleSave} shape='round' className="theme-button primary" loading={saving}>
                            Save
                        </Button>
                    </Col>
                </Row>
            </AccessControl>
        </div>
    );
}
